import React, {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {TShow} from '../../../../hooks/useShow';
import {TEticketEvent, TEticketSession} from '../../../../redux/eTicket/eTicket.type';
import {getDateFormat, getHourMinutes, tsToDate} from '../../../../library/dates';
import {useETicketDir} from '../../hooks/useETicketDir';
import SheetShell from '../SheetShell/SheetShell';
import * as css from './ScheduleSheet.css';

interface ScheduleSheetProps {
    show: TShow;
    sessions: TEticketSession[];
    event: TEticketEvent;
}

type DayGroup = {
    key: string;
    weekday: string;
    date: string;
    sessions: TEticketSession[];
};

/* The sessions (lectures / shows) booked on this ticket of a timetable event, grouped by day and
   ordered by start time — the new ticket's counterpart of the legacy ticket's "תכנית ההרצאות" list.
   Times are shown in the event's timezone, like every other time on the page. */
function ScheduleSheet({show, sessions, event}: ScheduleSheetProps): React.ReactElement {
    const {t} = useTranslation('eTicket');
    const {lang} = useETicketDir();

    const days = useMemo((): DayGroup[] => {
        const groups: DayGroup[] = [];
        const sorted = [...sessions].sort((a, b) => (a.time ?? 0) - (b.time ?? 0));
        sorted.forEach(session => {
            const key = session.time ? getDateFormat(session.time, 'YYYY-MM-DD', event.timeZone) : '';
            let group = groups.find(g => g.key === key);
            if(!group) {
                group = {
                    key,
                    // The weekday name is localized through tsToDate — getDateFormat applies no locale
                    weekday: session.time ? tsToDate(session.time, lang, event.timeZone, 'dddd') : '',
                    date: session.time ? getDateFormat(session.time, 'DD.MM.YYYY', event.timeZone) : '',
                    sessions: []
                };
                groups.push(group);
            }
            group.sessions.push(session);
        });
        return groups;
    }, [sessions, event.timeZone, lang]);

    return (
        <SheetShell show={show} title={t('scheduleTitle')} data-cy="schedule-sheet">
            <css.list>
                {days.map(day => (
                    <css.day key={day.key || 'undated'} data-cy="schedule-day">
                        {day.date && (
                            <css.dayTitle data-cy="schedule-day-title">
                                {day.weekday ? t('scheduleDay', {weekday: day.weekday, date: day.date}) : day.date}
                            </css.dayTitle>
                        )}
                        {day.sessions.map(session => (
                            <css.row key={session.ttid} $color={session.color} data-cy={`schedule-session-${session.ttid}`}>
                                <css.time>{session.time ? getHourMinutes(session.time, event.timeZone) : '—'}</css.time>
                                <css.text>
                                    <css.title>{session.title}</css.title>
                                    {session.speakers && <css.meta data-cy="schedule-session-speakers">{session.speakers}</css.meta>}
                                    {session.place && <css.meta data-cy="schedule-session-place">{session.place}</css.meta>}
                                    {session.amount > 1 && <css.meta>{t('schedulePlaces', {count: session.amount})}</css.meta>}
                                    {session.link?.url && (
                                        <css.link href={session.link.url} target="_blank" rel="noopener noreferrer">
                                            {session.link.title || session.link.url}
                                        </css.link>
                                    )}
                                </css.text>
                            </css.row>
                        ))}
                    </css.day>
                ))}
            </css.list>
        </SheetShell>
    );
}

export default ScheduleSheet;
